import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { signinFormDefaultValue, signinFormType, getSigninFormValidationSchema } from './utils';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui-kit/form';
import { Input } from '@/components/ui-kit/input';
import { Button } from '@/components/ui-kit/button';
import { useAuthStore } from '@/state/store/auth';
import { useLogin } from '@/api-client/use-login';
import { Captcha, ErrorAlert, PasswordInput, useCaptcha } from '@/components/core';

export const SigninEmail = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { login } = useAuthStore();

  const googleSiteKey = import.meta.env.VITE_CAPTCHA_SITE_KEY || '';
  const captchaEnabled = googleSiteKey !== '';

  const { execute, captcha, reset: resetCaptcha } = useCaptcha({
    siteKey: googleSiteKey,
    type: "v3",
  });

  const form = useForm({
    defaultValues: signinFormDefaultValue,
    resolver: zodResolver(getSigninFormValidationSchema(t)),
    mode: 'onChange',
  });

  const { isPending, mutateAsync, isError } = useLogin();

  const isSubmitDisabled = isPending;

  const onSubmitHandler = async (values: signinFormType) => {
    try {
      const captchaToken = captchaEnabled ? (await execute()) || '' : '';
      const res = await mutateAsync({
        username: values.username,
        password: values.password,
        captcha_code: captchaToken,
      });

      if ((res as any).enable_mfa)
        return navigate(
          `/verify-mfa?mfa_id=${(res as any)?.mfaId}&mfa_type=${(res as any)?.mfaType}&user_name=${values.username}`
        );

      login();
      // Drop any cached data from a previously signed-in account (getAccount,
      // organizations, etc.) so the new session fetches fresh — otherwise a
      // stale cached account briefly repopulates the store after login.
      queryClient.clear();
      navigate('/');
    } catch {
      // Inline ErrorAlert (driven by isError) is the single source of truth
      // for failed sign-in. Avoid a second toast that could leak backend
      // details and enable account enumeration.
      resetCaptcha();
    }
  };

  return (
    <div className="w-full">
      <ErrorAlert
        isError={isError}
        title={t('INVALID_CREDENTIALS')}
        message={t('EMAIL_PASSWORD_NOT_VALID')}
      />
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmitHandler)} className="space-y-4">
          <FormField
            control={form.control}
            name="username"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('EMAIL')}</FormLabel>
                <FormControl>
                  <Input autoComplete="username" placeholder={t('ENTER_YOUR_EMAIL')} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('PASSWORD')}</FormLabel>
                <FormControl>
                  <PasswordInput autoComplete="current-password" placeholder={t('ENTER_YOUR_PASSWORD')} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex justify-end">
            <Link
              to="/forgot-password"
              className="text-sm text-primary hover:text-primary-600 hover:underline"
            >
              {t('FORGOT_PASSWORD')}
            </Link>
          </div>

          {captchaEnabled && (
            <div className="my-4">
              <Captcha {...captcha} theme="light" size="normal" />
            </div>
          )}

          <Button type="submit" className="w-full" disabled={isSubmitDisabled}>
            {t('LOG_IN')}
          </Button>
        </form>
      </Form>
    </div>
  );
};
